/**
 * English locale for messaging-ui.
 * Export is the "messaging" subtree: host apps merge this into their i18n
 * resources as translation.messaging so that t('messaging.list.loading') etc. resolve.
 */
import type { WidenLocale } from '@mangovoice/types'

export const messagingEn = {
  list: {
    loading: `Loading message groups...`,
    error: `Error`,
    empty: `No message groups found`,
    emptyDescription: `There are currently no message groups configured for this PBX.`,
    deleteConfirmTitle: `Delete message group`,
    deleteConfirm: `Are you sure you want to delete "{{name}}"?`,
    smsEnabled: `Enabled`,
    smsDisabled: `Disabled`,
    delete: `Delete`,
    columnGroupName: `Group Name`,
    columnPrimarySms: `Primary SMS`,
    columnMembers: `Members`,
    columnSmsStatus: `SMS Status`,
    columnCreated: `Created`,
    columnUpdated: `Updated`,
    columnActions: `Actions`,
    toast: {
      createdTitle: `Message group created`,
      createdDescription: `"{{name}}" has been created successfully.`,
      createFailedTitle: `Create failed`,
      createFailedDescription: `Failed to create message group`,
      deletedTitle: `Message group deleted`,
      deletedDescription: `"{{name}}" has been deleted.`,
      deleteFailedTitle: `Delete failed`,
      deleteFailedDescription: `Failed to delete message group`,
    },
    title: `Messaging`,
    createButton: `+ Create Group`,
    searchPlaceholder: `Search by name or phone number...`,
    noResults: `No results`,
    noResultsDescription: `No message groups matching "{{search}}".`,
    noMembersWarning: `This group has no members. Customer messages may go unread.`,
  },
  detail: {
    validationErrorTitle: `Validation Error`,
    validationErrorDescription: `Group name is required`,
    toast: {
      updatedTitle: `Message group updated`,
      updateFailedTitle: `Update failed`,
      updateFailedDescription: `Failed to update message group`,
      smsAddedTitle: `SMS number added`,
      smsAddFailedTitle: `Failed to add SMS number`,
      smsRemovedTitle: `SMS number removed`,
      smsRemoveFailedTitle: `Failed to remove SMS number`,
      memberAddedTitle: `Member added`,
      memberAddFailedTitle: `Failed to add member`,
      memberAddFailedDescription: `Failed to add member to the group`,
      memberRemovedTitle: `Member removed`,
      memberRemoveFailedTitle: `Failed to remove member`,
    },
    loading: `Loading message group...`,
    error: `Error`,
    notFound: `Message group not found.`,
    title: `Edit Message Group`,
    viewTitle: `Message Group`,
    groupNameLabel: `Group Name`,
    groupNamePlaceholder: `Enter group name`,
    primarySmsLabel: `Primary SMS number`,
    primarySmsPlaceholder: `Select primary SMS number`,
    campaignIdWarning: `The selected number is missing a campaign ID and may not be able to send SMS, unless it is a toll-free service or a Canadian number.`,
    associatedSmsLabel: `Secondary SMS numbers`,
    noCampaignId: `(No campaign ID)`,
    removeSmsAria: `Remove SMS number`,
    addSmsNumberAria: `Add SMS number`,
    unsavedChanges: `Unsaved changes`,
    campaignSummaryTitle: `{{count}} number(s) missing campaign ID`,
    campaignSummaryDescription: `These numbers may not be able to send SMS unless they are toll-free or Canadian numbers.`,
    backToList: `← Back to list`,
    updating: `Updating...`,
    updateButton: `Update Message Group`,
    noMembersWarning: `This group has no members. Customer messages may go unread.`,
  },
  createModal: {
    validationErrorTitle: `Validation Error`,
    validationErrorDescription: `Please enter a name for the message group`,
    title: `Create Message Group`,
    nameLabel: `Group Name`,
    namePlaceholder: `Enter group name`,
    primarySmsLabel: `Primary SMS number`,
    primarySmsPlaceholder: `Select a number`,
    primarySmsRequired: `Please select a primary SMS number`,
    cancel: `Cancel`,
    creating: `Creating...`,
    createButton: `Create Group`,
  },
  members: {
    groupMembersTitle: `Group Members`,
    emptyGroup: `No members in this group yet.`,
    columnName: `Name`,
    columnExtension: `Extension`,
    columnAdded: `Added`,
    availableMembersTitle: `Available Members`,
    emptyAvailable: `No available members to add.`,
    removeMemberAria: `Remove member`,
    addMemberAria: `Add member`,
    searchPlaceholder: `Search by name or extension...`,
    noSearchResults: `No members match your search.`,
  },
  smsModal: {
    validationErrorTitle: `Validation Error`,
    validationErrorDescription: `Please select an SMS number`,
    title: `Associated SMS Numbers`,
    label: `Associated SMS Numbers`,
    loading: `Loading...`,
    selectPlaceholder: `Select a number`,
    submitting: `Submitting...`,
    submit: `Submit`,
    close: `Close`,
  },
  pagination: {
    resultsPerPage: `Results per page:`,
    previousPage: `Previous page`,
    nextPage: `Next page`,
    jumpToPage: `Jump to page {{page}}`,
    all: `All`,
  },
} as const

export type MessagingLocale = WidenLocale<typeof messagingEn>
